import { type AuthState, AuthContext } from "./auth.helpers";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, azureLoginUrl, type UserOut } from "./lib/api";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserOut | null>(null);
  const [isHydrating, setIsHydrating] = useState<boolean>(true);

  // Probe the session cookie once on mount. After the SSO callback redirects
  // back to the SPA the cookie is already set, so this populates the user.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.me();
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // api.ts dispatches this when any request gets a 401 (session expired/revoked).
  useEffect(() => {
    const onCleared = () => setUser(null);
    window.addEventListener("sci:auth-cleared", onCleared);
    return () => window.removeEventListener("sci:auth-cleared", onCleared);
  }, []);

  const login = useCallback(() => {
    // Full-page redirect into the Azure OIDC flow on the backend.
    window.location.href = azureLoginUrl();
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // best-effort; clear locally regardless
    }
    setUser(null);
    window.location.href = "/";
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      isAuthenticated: !!user,
      isHydrating,
      login,
      logout,
    }),
    [user, isHydrating, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
